'use client';

// Reusable sort component based on Figma design

import { useState, useEffect, JSX } from 'react';
import { tv } from 'tailwind-variants';
import { getTestProps } from 'lib/testing/utils';
import useDictionary from 'lib/hooks/useDictionary';
import { RemixIcon } from 'helpers/RemixIcon';

export type SortOption = {
  id: string;
  label: string;
  value: string;
};

export type SearchSortProps = {
  options: SortOption[];
  selectedOption?: string;
  onSortChange?: (optionId: string, value: string) => void;
  className?: string;
  label?: string;
  tailwindVariants?: {
    position?: 'Default' | 'ArticleSearch';
  };
};

const SearchSort = ({
  options,
  selectedOption,
  onSortChange,
  tailwindVariants,
}: SearchSortProps): JSX.Element => {
  const { getDictionaryValue } = useDictionary();
  const [isOpen, setIsOpen] = useState(false);

  // Set default selected option to first option if none selected
  const defaultSelectedOption = selectedOption || (options.length > 0 ? options[0].id : '');
  const selectedOptionData = options.find((option) => option.id === defaultSelectedOption);

  const handleOptionSelect = (optionId: string, value: string) => {
    onSortChange?.(optionId, value);
    setIsOpen(false);
  };

  // Auto-select first option if no option is selected
  useEffect(() => {
    if (!selectedOption && options.length > 0) {
      const firstOption = options[0];
      onSortChange?.(firstOption.id, firstOption.value);
    }
  }, [selectedOption, options, onSortChange]);

  const {
    base,
    sorLabel,
    dropdownWrapper,
    trigger,
    triggerText,
    triggerIcon,
    dropdown,
    option,
    optionText,
  } = TAILWIND_VARIANTS(tailwindVariants);

  return (
    <div className={base(tailwindVariants)} {...getTestProps('search-sort')}>
      <span id="sort-label" className={sorLabel()}>
        {getDictionaryValue('SortBy') || 'Sort by'}
      </span>
      <div className={dropdownWrapper()}>
        <button
          type="button"
          className={trigger()}
          onClick={() => setIsOpen(!isOpen)}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={isOpen ? 'sort-dropdown' : undefined}
          aria-labelledby="sort-label"
          aria-label={getDictionaryValue('SortBy') || 'Sort by'}
          {...getTestProps('sort-trigger')}
        >
          <span className={triggerText()}>
            {selectedOptionData?.label || options[0]?.label || ''}
          </span>
          <span className={triggerIcon()}>
            {isOpen ? (
              <RemixIcon name="arrow-up-s-line" size="lg" />
            ) : (
              <RemixIcon name="arrow-down-s-line" size="lg" />
            )}
          </span>
        </button>

        {isOpen && (
          <div
            id="sort-dropdown"
            className={dropdown()}
            role="listbox"
            aria-label="Sort options"
            {...getTestProps('sort-dropdown')}
          >
            {options.map((sortOption) => (
              <button
                key={sortOption.id}
                type="button"
                className={option()}
                onClick={() => handleOptionSelect(sortOption.id, sortOption.value)}
                role="option"
                aria-label={sortOption.label}
                aria-selected={sortOption.id === defaultSelectedOption}
                {...getTestProps(`sort-option-${sortOption.id}`)}
              >
                <span className={optionText()}>{sortOption.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchSort;

const TAILWIND_VARIANTS = tv({
  slots: {
    base: [],
    sorLabel: [
      'w-fit',
      'font-typography-font-family-body',
      'text-color-grayscale-600',
      'text-typography-font-size-body-medium',
      'leading-typography-line-height-body-medium',
      'font-normal',
    ],
    dropdownWrapper: [
      'w-fit',
      'relative',
      'h-auto',
      'rounded-border-radius-radius-1',
      'border',
      'border-general-border-width-button',
      'border-color-grayscale-700',
      'bg-color-grayscale-white',
      'inline-block',
      'group',
    ],
    trigger: [
      'w-full',
      'flex',
      'flex-row',
      'items-center',
      'justify-between',
      'gap-padding-micro',
      'py-spacing-spacing-4 sm:py-spacing-spacing-8',
      'px-spacing-spacing-8 sm:px-spacing-spacing-12',
    ],
    triggerText: [
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'leading-typography-line-height-body-medium',
      'text-color-text-dark',
      'font-semibold',
    ],
    triggerIcon: ['w-8', 'h-8', 'text-p-color-icon', 'flex', 'items-center', 'justify-center'],
    dropdown: ['top-full', 'left-0', 'right-0', 'z-[10]'],
    option: [
      'w-full',
      'flex',
      'items-center',
      'justify-between',
      'py-spacing-spacing-4 sm:py-spacing-spacing-8',
      'px-spacing-spacing-8 sm:px-spacing-spacing-12',
      'text-left',
      'transition-colors',
      'duration-150',
      'bg-color-grayscale-white',
      'hover:bg-color-core-color-4-skyblue-lightTint',
    ],
    optionText: [
      'text-olor-text-dark',
      'font-typography-font-family-body',
      'text-typography-font-size-body-medium',
      'leading-typography-line-height-body-medium',
      'font-semibold',
      'truncate',
    ],
  },
  variants: {
    position: {
      Default: {
        base: [
          'absolute',
          'w-full',
          'flex',
          'flex-row',
          'justify-end',
          'items-start',
          'gap-spacing-spacing-8',
          'top-[-20px] sm:top-[-24px]',
          'right-0',
          'z-[10]',
        ],
        sorLabel: ['pt-spacing-spacing-12'],
        dropdown: ['relative'],
      },
      ArticleSearch: {
        base: ['flex', 'justify-end', 'items-center', 'gap-spacing-spacing-8'],
        sorLabel: [],
        dropdownWrapper: ['relative'],
        dropdown: ['absolute'],
      },
    },
  },
  defaultVariants: {
    position: 'Default',
  },
});
